import { useEffect, useRef } from 'react'
import { useClient } from '@/hooks/useClient'
import { logError } from '@/utils/errorUtils'
import { AUDIT_REPORT_DEBOUNCE_MS } from './useAuditSectionReporting'

/**
 * PD-1322 (S18) — records printing of a patient chart. The ticket lists "printed" among
 * the actions to capture (AC4) and requires prints to "remain recorded as normal audit
 * events" (AC12).
 *
 * WHAT THIS ACTUALLY CAPTURES: print INTENT — the moment the browser begins its print
 * flow, whether from Cmd/Ctrl+P, the browser menu, or a `window.print()` call. It is NOT
 * confirmation that paper came out, and cannot be: no browser exposes whether the user
 * confirmed or cancelled the print dialog, nor whether a printer accepted the job.
 * `afterprint` fires either way, so it distinguishes nothing. Anyone reading a "Printed
 * patient chart" row should read it as "this chart was sent to the print dialog".
 *
 * `beforeprint` is the signal rather than `matchMedia('print')`: Safari fires no
 * `beforeprint`, but it also reports no stable `print` media match outside an actual
 * paint, and layering both would double-report on every browser that supports both.
 * Chrome, Edge and Firefox all fire `beforeprint`; the Safari gap is listed as a known
 * limitation on PD-1322 rather than papered over with an unreliable second signal.
 *
 * SUPPRESSION, NOT DELAY. The other reporters debounce on the trailing edge because they
 * fire on things the user flicks through — sections, documents — and the point is to skip
 * what was skimmed. Printing is different in two ways: `beforeprint` is already a
 * deliberate act, and the print dialog that follows it is modal, so a trailing-edge timer
 * would sit unfired for as long as the dialog is open and be thrown away entirely if the
 * user then navigated off the chart. That trades a lost audit record for nothing. So this
 * reports on the FIRST event and suppresses repeats for AUDIT_REPORT_DEBOUNCE_MS (a
 * preview that re-fires, a held Cmd+P), plus at most one print per patient chart visit —
 * same guarantee as the other reporters, without the window to lose the event in.
 *
 * The client reports only the action code; the server authors the record.
 */
export function useAuditPrintReporting(patientId: string | undefined): void {
  const client = useClient()
  const reportedRef = useRef<string | undefined>(undefined)
  const lastAttemptRef = useRef(0)

  useEffect(() => {
    if (!patientId) return

    const onBeforePrint = () => {
      const now = Date.now()
      if (reportedRef.current === patientId) return
      if (now - lastAttemptRef.current < AUDIT_REPORT_DEBOUNCE_MS) return
      lastAttemptRef.current = now
      // Mark before the request so a slow response cannot double-report; unmark on
      // failure so a retry is possible on the next print.
      reportedRef.current = patientId
      client.auditReportAction({ patientId, action: 'chart-print' }).catch((err) => {
        reportedRef.current = undefined
        logError('patient', 'Failed to report chart print', err)
      })
    }

    window.addEventListener('beforeprint', onBeforePrint)
    return () => window.removeEventListener('beforeprint', onBeforePrint)
  }, [client, patientId])
}
